// AXA PUNCTATĂ A CAROSABILULUI, la capete, colțuri și treceri.
//
// Asfaltul vine gata ROTUNJIT din motor (`positioning_plan/backend/engine/
// circulatie/rotunjire.py`, 28.09.2026): colțurile intersecțiilor cu 5 m,
// capetele cu 1,5 m, S-ul între două alei paralele decalate — și locurile,
// verdele și bilanțul sunt măsurate pe el. Planșa umple poligoanele primite.
//
// Ce rămâne de desenat aici e AXA PUNCTATĂ (sensul dublu), ca traseu, nu ca
// segment pe fiecare piesă:
//   * la COLȚ, cele două axe se leagă printr-un arc concentric cu racordarea
//     motorului (raza RACORD + w/2 = 8 m);
//   * la T, drumul care se termină își oprește axa cu RACORD înaintea
//     celuilalt și se leagă de axa lui cu câte un arc de 8 m pe fiecare parte;
//     axa drumului drept rămâne întreagă (cine nu schimbă direcția);
//   * la CRUCE, la fel, din amândouă părțile (patru arce), iar axa trece
//     dreaptă și peste celălalt drum;
//   * la ÎNTOARCERE — palierul rampei lipit de alee, amândouă terminate pe
//     aceeași parte — un semicerc între cele două axe, cum întoarce o mașină
//     de pe alee în rampă (Oana, 28.09.2026, print screen 1);
//   * la S — o alee care se continuă în alta paralelă, decalată — o curbă în S
//     între cele două axe, pe lungimea suprapunerii (print screen 2).
//
// Totul în metri de desen, în cadrul planșei; formele sunt șiruri `d` de SVG.
//
// VARIANTA FĂRĂ CURBURI (`diagnostics.circulatie.varianta === "fara_curburi"`,
// doar pe ecranul de încercări): asfaltul are colțurile drepte, deci nici axa nu
// se mai arcuiește (Oana, 30.09.2026). La colț și la T axa drumului care se
// termină merge DREAPTĂ până la axa celuilalt; la întoarcere cele două axe merg
// până la capăt și se leagă printr-un segment; la S, un segment oblic de pe o
// axă pe cealaltă, pe suprapunere.
//
// NICIO CURBĂ ÎN AFARA ASFALTULUI (Oana, 01.10.2026): arcele de mai sus presupun
// că asfaltul are racordarea acolo, adică drumul celălalt continuă cel puțin cât
// raza arcului. Nu e mereu așa — o bară de viraj scurtă, o piesă mică lângă
// capătul unei alei, un T la 3 m de colț — și arcul ieșea în verde. Fiecare
// curbă e încercată pe poligoanele carosabilului (`asfalt`), din 10 în 10 cm, și
// cea care iese e lăsată deoparte; axa dreaptă rămâne cum e.
//
// Tot pe asfalt se hotărăște și dacă un capăt e LEGAT de drumul de lângă el: o
// alee pe care motorul a închis-o cu un capăt rotunjit la o jumătate de metru de
// altă alee e o fundătură, nu un colț (Oana, 01.10.2026, print screen 2).
//
// AXA SE OPREȘTE LA MUCHIA CELUILALT DRUM (Oana, 30.10.2026): împerecherea de
// mai sus știe doar axele DREPTE, iar agentul planului de situație scrie și alei
// cu arce (`axis` + `bulges`) sau frânte, pe care liniuțele treceau prin
// intersecții și ieșeau dincolo de capete. De aceea, după împerechere, FIECARE
// axă — dreaptă sau traseu curb — se taie generic: de la fiecare capăt spre
// interior, cât stă în corpul (poligonul) altui drum, ca o linie de marcaj care
// se oprește la bordura drumului în care intră; și nicio liniuță nu se
// desenează în afara asfaltului (reuniunea drumurilor și rampelor) — bucata
// care iese se taie, nu se mută. Arcele de legătură de mai sus rămân cum sunt:
// ele stau în intersecție dinadins.
//
// SENSUL UNIC (Oana, 30.10.2026): o alee cu `two_way: false` — dreaptă sau
// curbă — nu are linie punctată pe axă (linia desparte două benzi care merg
// în sensuri opuse, și ea n-are decât una); în schimb poartă săgeți de sens
// pe asfalt, așezate PE axă, în sensul ordinii vârfurilor ei (`axis[0] →
// axis[last]`, cum a scris-o agentul), câte una la ~`SAGEATA_PAS` și cel puțin
// una pe fiecare bucată de axă rămasă după tăierea de mai sus — deci nici ele
// nu cad în intersecții sau în afara asfaltului. Înainte, aleea curbă cu sens
// unic a agentului se desena ca una cu dublu sens: liniuțe pe mijloc și nicio
// săgeată (`siteSignage` nu vede traseele curbe).

import type { Pt } from "../types";
import type { RoadAxis } from "./siteDerive";

/** Raza racordării concave din motor (`rotunjire.RAZA`): arcul axei e concentric cu ea. */
export const RACORD = 5.0;
/** Cât se întind capetele S-ului dincolo de suprapunerea celor două alei. */
const S_INTINDERE = 2.0;
/** Cât de aproape de un drum trebuie să fie capătul altuia ca să fie «în el». */
const TOL = 0.35;
/** Un capăt la atât de limita lotului e o poartă spre stradă, nu un final. */
const LA_LIMITA = 1.5;
/** Cât poate ieși o curbă peste muchia asfaltului: rotunjirile sunt poligoane. */
const PESTE_MUCHIE = 0.1;
/** Din cât în cât e încercată o curbă pe asfalt: sub cea mai îngustă fâșie de verde. */
const PAS_PROBA = 0.1;
/** Cât de adânc trebuie să intre un punct al axei în corpul altui drum ca să fie
 *  «în el»: piesele vecine se ating pe muchie, iar o axă care se termină exact pe
 *  muchia celuilalt drum nu intră în el. */
const IN_CORP = 0.05;
/** Sub atât o bucată de axă rămasă după tăieri nu se mai desenează: o liniuță
 *  singură între două intersecții se citește ca murdărie, nu ca marcaj. */
const AXA_MIN = 1.0;
/** Din cât în cât se pune o săgeată de sens pe o alee cu sens unic. */
const SAGEATA_PAS = 15.0;
/** Lungimea plăcii săgeții (`RoadArrowMark`, 2,55 m) plus un pas: pe o bucată
 *  de axă mai scurtă săgeata ar ieși din bucată, adică în intersecție. */
const SAGEATA_LOC_MIN = 3.0;

export type FelCapat = "final" | "colt" | "T" | "drept";

export interface Capat {
  /** Drumul și capătul lui: 1 = `(x1, y1)`, 2 = `(x2, y2)`. */
  i: number; e: 1 | 2;
  fel: FelCapat;
  /** Drumul în care se termină (colț, T). */
  j?: number;
  /** La T: drumul continuă dincolo (cruce), deci axa punctată trece. */
  cruce?: boolean;
}

/** O pereche de drumuri paralele care se leagă altfel decât în colț. */
export interface Pereche {
  i: number; j: number;
  fel: "intoarcere" | "S";
}

/** O alee cu axa curbă sau frântă (arce din `bulges`, polilinie): liniuțele merg
 *  pe traseul ei, fără împerecherea axelor drepte. `corp` e propriul poligon,
 *  ca să nu se taie axa în el. */
export interface Traseu { linie: Pt[]; corp?: number[][];
  /** `false` = sens unic: fără liniuțe, cu săgeți de sens (v. nota din antet).
   *  Lipsă = dublu sens. */
  twoWay?: boolean }

/** O săgeată de sens pe asfalt: centrul și direcția de mers, în convenția
 *  glifei (`RoadArrowMark`: `deg` = heading, (sin, −cos)). */
export interface Sageata { x: number; y: number; deg: number }

export interface Carosabil {
  /** Axele punctate, ca `d` de SVG (planșa). */
  axe: string[];
  /** ACELEAȘI axe, ca polilinii în metri (macheta): o singură derivare, două
   *  desene — marcajul de pe asfalt nu poate diferi între planșă și machetă. */
  linii: Pt[][];
  /** Săgețile de sens ale aleilor cu sens unic, pe axa lor tăiată. */
  sageti: Sageata[];
  capete: Capat[];
  perechi: Pereche[];
}

type Cadru = { ux: number; uy: number; nx: number; ny: number; L: number; h: number };

const cadru = (a: RoadAxis): Cadru => {
  const dx = a.x2 - a.x1, dy = a.y2 - a.y1;
  const L = Math.hypot(dx, dy) || 1;
  const ux = dx / L, uy = dy / L;
  return { ux, uy, nx: -uy, ny: ux, L, h: a.w / 2 };
};

/** Coordonatele unui punct în cadrul unei axe: `s` de-a lungul, `t` de-a latul. */
const inCadru = (a: RoadAxis, c: Cadru, p: Pt) => ({
  s: (p[0] - a.x1) * c.ux + (p[1] - a.y1) * c.uy,
  t: (p[0] - a.x1) * c.nx + (p[1] - a.y1) * c.ny,
});

const punct = (a: RoadAxis, c: Cadru, s: number, t: number): Pt =>
  [a.x1 + s * c.ux + t * c.nx, a.y1 + s * c.uy + t * c.ny];

const f = (v: number) => Number(v.toFixed(3));
const P = (p: Pt) => `${f(p[0])} ${f(p[1])}`;

function distantaLaContur(p: Pt, lot: Pt[]): number {
  let best = Infinity;
  for (let i = 0; i < lot.length; i++) {
    const a = lot[i], b = lot[(i + 1) % lot.length];
    const vx = b[0] - a[0], vy = b[1] - a[1];
    const l2 = vx * vx + vy * vy;
    const tt = l2 < 1e-12 ? 0 : Math.max(0, Math.min(1, ((p[0] - a[0]) * vx + (p[1] - a[1]) * vy) / l2));
    best = Math.min(best, Math.hypot(a[0] + tt * vx - p[0], a[1] + tt * vy - p[1]));
  }
  return best;
}

function inInel(p: Pt, ring: number[][]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i], [xj, yj] = ring[j];
    if ((yi > p[1]) !== (yj > p[1])
        && p[0] < ((xj - xi) * (p[1] - yi)) / (yj - yi || 1e-12) + xi) inside = !inside;
  }
  return inside;
}

/** Punctul e pe unul din inele (sau la `PESTE_MUCHIE` de muchia lui). */
const peAsfalt = (p: Pt, inele: number[][][]) =>
  inele.some((r) => inInel(p, r) || distantaLaContur(p, r as Pt[]) <= PESTE_MUCHIE);

/** Toată linia frântă e pe asfalt; fără asfalt dat, e mereu. */
function totPeAsfalt(puncte: Pt[], inele: number[][][]): boolean {
  if (inele.length === 0) return true;
  for (let k = 0; k + 1 < puncte.length; k++) {
    const [a, b] = [puncte[k], puncte[k + 1]];
    const n = Math.max(1, Math.ceil(Math.hypot(b[0] - a[0], b[1] - a[1]) / PAS_PROBA));
    for (let m = 0; m <= n; m++) {
      if (!peAsfalt([a[0] + ((b[0] - a[0]) * m) / n, a[1] + ((b[1] - a[1]) * m) / n], inele)) return false;
    }
  }
  return true;
}

/** Punctul e ÎN corpul unui drum străin: înăuntru și la mai mult de `IN_CORP`
 *  de muchia lui (propriul corp, dat prin referință, nu contează). */
const inCorpStrain = (p: Pt, corpuri: number[][][], propriu?: number[][]) =>
  corpuri.some((c) => c !== propriu && c.length >= 3 && inInel(p, c)
                      && distantaLaContur(p, c as Pt[]) > IN_CORP);

/** Poligonul CONVEX lărgit cu `m` pe toate laturile: fiecare latură mutată în
 *  afară cu `m`, vârful nou la tăietura laturilor vecine. Pentru banda unei
 *  treceri de pietoni (un dreptunghi), ca golul din axă să înceapă cu `m`
 *  înaintea zebrei, cât să nu atingă plăcuța săgeții. Un poligon degenerat
 *  (arie nulă) se întoarce cum e. */
export function dilata(ring: number[][], m: number): number[][] {
  const n = ring.length;
  if (n < 3 || m === 0) return ring;
  let aria = 0;
  for (let i = 0; i < n; i++) {
    const a = ring[i], b = ring[(i + 1) % n];
    aria += a[0] * b[1] - b[0] * a[1];
  }
  if (Math.abs(aria) < 1e-9) return ring;
  const semn = aria > 0 ? 1 : -1;                   // orientarea: normala exterioară e la dreapta pe ocolul antiorar
  const linii = ring.map((a, i) => {
    const b = ring[(i + 1) % n];
    const dx = b[0] - a[0], dy = b[1] - a[1], L = Math.hypot(dx, dy) || 1;
    const nx = (dy / L) * semn, ny = (-dx / L) * semn;
    return { p: [a[0] + nx * m, a[1] + ny * m], d: [dx / L, dy / L] };
  });
  return ring.map((_, i) => {
    const l1 = linii[(i - 1 + n) % n], l2 = linii[i];
    const det = l1.d[0] * l2.d[1] - l1.d[1] * l2.d[0];
    if (Math.abs(det) < 1e-9) return [l2.p[0], l2.p[1]];   // laturi paralele: punctul mutat al laturii
    const t = ((l2.p[0] - l1.p[0]) * l2.d[1] - (l2.p[1] - l1.p[1]) * l2.d[0]) / det;
    return [l1.p[0] + l1.d[0] * t, l1.p[1] + l1.d[1] * t];
  });
}

/** Linia frântă îndesită din `PAS_PROBA` în `PAS_PROBA`, fiecare probă cu
 *  segmentul (`k`) pe care stă; vârfurile sunt și ele probe. */
function probele(linie: Pt[]): { p: Pt; k: number }[] {
  const out: { p: Pt; k: number }[] = [];
  for (let k = 0; k + 1 < linie.length; k++) {
    const [a, b] = [linie[k], linie[k + 1]];
    const n = Math.max(1, Math.ceil(Math.hypot(b[0] - a[0], b[1] - a[1]) / PAS_PROBA));
    for (let m = 0; m < n; m++) {
      out.push({ p: [a[0] + ((b[0] - a[0]) * m) / n, a[1] + ((b[1] - a[1]) * m) / n], k });
    }
  }
  if (linie.length >= 2) out.push({ p: linie[linie.length - 1], k: linie.length - 2 });
  return out;
}

/** Bucata liniei dintre probele `i0` și `i1`: capetele tăiate, vârfurile
 *  originale între ele (nu probele, ca `d`-ul să rămână scurt). */
function bucata(linie: Pt[], probe: { p: Pt; k: number }[], i0: number, i1: number): Pt[] {
  const out: Pt[] = [probe[i0].p];
  for (let j = probe[i0].k + 1; j <= probe[i1].k; j++) out.push(linie[j]);
  out.push(probe[i1].p);
  return out;
}

const lungime = (l: Pt[]) => {
  let L = 0;
  for (let k = 0; k + 1 < l.length; k++) L += Math.hypot(l[k + 1][0] - l[k][0], l[k + 1][1] - l[k][1]);
  return L;
};

/** Punctul de la lungimea `s` pe linia frântă și direcția ei acolo. */
function laLungime(l: Pt[], s: number): { p: Pt; dir: Pt } {
  let rest = s;
  for (let k = 0; k + 1 < l.length; k++) {
    const [a, b] = [l[k], l[k + 1]];
    const L = Math.hypot(b[0] - a[0], b[1] - a[1]);
    if (rest <= L || k + 2 === l.length) {
      const f = L > 1e-9 ? Math.min(1, Math.max(0, rest / L)) : 0;
      const dir: Pt = L > 1e-9 ? [(b[0] - a[0]) / L, (b[1] - a[1]) / L] : [1, 0];
      return { p: [a[0] + (b[0] - a[0]) * f, a[1] + (b[1] - a[1]) * f], dir };
    }
    rest -= L;
  }
  return { p: l[0], dir: [1, 0] };
}

/** Săgețile de sens pe o bucată de axă cu sens unic, în sensul ei: una la
 *  ~`SAGEATA_PAS`, cel puțin una, la mijlocul fiecărui interval — deci niciuna
 *  mai aproape de capătul bucății decât jumătate de interval, ca placa să nu
 *  iasă în intersecția la care s-a oprit axa. Bucata sub `SAGEATA_LOC_MIN`
 *  nu încape nici o săgeată. */
export function sagetile(bucata: Pt[]): Sageata[] {
  const L = lungime(bucata);
  if (L < SAGEATA_LOC_MIN) return [];
  const n = Math.max(1, Math.round(L / SAGEATA_PAS));
  return Array.from({ length: n }, (_, k) => {
    const { p, dir } = laLungime(bucata, (L * (k + 0.5)) / n);
    // headingul glifei: `rotate(deg)` duce −y în (sin deg, −cos deg)
    return { x: f(p[0]), y: f(p[1]), deg: f((Math.atan2(dir[0], -dir[1]) * 180) / Math.PI) };
  });
}

/** Axa tăiată generic (v. nota din antet): ruptă acolo unde iese de pe asfalt
 *  (`inele`), apoi fiecare bucată scurtată de la capete spre interior cât stă
 *  în corpul altui drum (`corpuri`, fără `propriu`); bucățile sub `AXA_MIN` se
 *  lasă deoparte. Fără corpuri și fără asfalt, linia rămâne cum e. */
export function taieAxa(linie: Pt[], corpuri: number[][][], inele: number[][][],
                        propriu?: number[][], goluri: number[][][] = []): Pt[][] {
  if (linie.length < 2) return [];
  if (corpuri.length === 0 && inele.length === 0 && goluri.length === 0) return [linie];
  const probe = probele(linie);
  const strain = (i: number) => inCorpStrain(probe[i].p, corpuri, propriu);
  const out: Pt[][] = [];
  // o rulare de probe de pe asfalt, cu capetele trase înăuntru cât stau în
  // alt drum; capătul care cade EXACT pe muchia celuilalt drum (asfaltul se
  // termină acolo) se judecă după proba următoare, altfel ar rămâne o liniuță
  // pe muchie
  const taie = (i0: number, i1: number) => {
    while (i0 < i1 && (strain(i0) || strain(i0 + 1))) i0++;
    while (i1 > i0 && (strain(i1) || strain(i1 - 1))) i1--;
    if (i1 > i0) {
      const b = bucata(linie, probe, i0, i1);
      if (lungime(b) >= AXA_MIN) out.push(b);
    }
  };
  let start: number | null = null;
  for (let i = 0; i <= probe.length; i++) {
    // GOLURILE (Oana, 31.10.2026): trecerea de pietoni rupe axa ca marginea asfaltului,
    // altfel săgeata de sens sau liniuțele cădeau peste zebră
    const ok = i < probe.length && (inele.length === 0 || peAsfalt(probe[i].p, inele))
      && !goluri.some((g) => g.length >= 3 && inInel(probe[i].p, g));
    if (ok && start === null) start = i;
    if (!ok && start !== null) { taie(start, i - 1); start = null; }
  }
  return out;
}

/** Ce e la fiecare capăt de drum. Cu `asfalt`, un colț sau un T se recunoaște
 *  doar dacă asfaltul leagă capătul de axa celuilalt drum. */
export function capetele(axe: RoadAxis[], rampe: number[][][] = [], lot: Pt[] = [],
                         asfalt: number[][][] = []): Capat[] {
  const inele = asfalt.filter((r) => r.length >= 3);
  const cadre = axe.map(cadru);
  const out: Capat[] = [];
  axe.forEach((a, i) => {
    const c = cadre[i];
    for (const e of [1, 2] as const) {
      const p: Pt = e === 1 ? [a.x1, a.y1] : [a.x2, a.y2];
      const afara: Pt = e === 1 ? [-c.ux, -c.uy] : [c.ux, c.uy];
      const dincolo: Pt = [p[0] + afara[0] * 0.3, p[1] + afara[1] * 0.3];
      // o gură de rampă sau limita lotului: drumul continuă, capătul e drept
      if (rampe.some((r) => r.length >= 3 && inInel(dincolo, r))
          || (lot.length >= 3 && distantaLaContur(p, lot) <= LA_LIMITA)) {
        out.push({ i, e, fel: "drept" });
        continue;
      }
      let gasit: Capat | null = null;
      let continua = false;
      axe.forEach((b, j) => {
        if (j === i || gasit || continua) return;
        const cb = cadre[j];
        const paralel = Math.abs(c.ux * cb.ux + c.uy * cb.uy) > 0.9;
        const q = inCadru(b, cb, p);
        // capătul stă în lateralul lui `b` (sau la un pas de el, cât jumătate din
        // propria lățime: la colț, capătul celuilalt se oprește la muchia asta)
        const tol = paralel ? TOL : c.h + TOL;
        if (Math.abs(q.t) > cb.h + tol || q.s < -tol || q.s > cb.L + tol) return;
        if (paralel) { continua = true; return; }
        // capătul care se oprește ÎNAINTEA axei lui `b` trebuie să ajungă pe
        // asfalt până în `b` (o jumătate de metru dincolo de muchia lui); altfel
        // e o fundătură lângă el. Cel care trece de axa lui `b` (colțul) e legat
        // prin propria piesă — colțul exterior poate fi teșit de motor, deci nu
        // se încearcă acolo.
        const peAxaB = punct(b, cb, q.s, 0);
        const inainte = (peAxaB[0] - p[0]) * afara[0] + (peAxaB[1] - p[1]) * afara[1];
        const inB = Math.max(0, inainte - (cb.h - 0.5));
        if (inainte > 0 && !totPeAsfalt([p, [p[0] + afara[0] * inB, p[1] + afara[1] * inB]], inele)) return;
        const laCapat = q.s < c.h + TOL || q.s > cb.L - c.h - TOL;
        if (laCapat) { gasit = { i, e, fel: "colt", j }; return; }
        // T: dincolo de `b`, pe aceeași linie, mai e un drum? atunci e o cruce
        const dupa: Pt = [p[0] + afara[0] * (b.w + 0.5), p[1] + afara[1] * (b.w + 0.5)];
        const cruce = axe.some((d, k) => {
          if (k === i || k === j) return false;
          const cd = cadre[k];
          if (Math.abs(c.ux * cd.ux + c.uy * cd.uy) < 0.9) return false;
          const qd = inCadru(d, cd, dupa);
          return Math.abs(qd.t) <= cd.h + TOL && qd.s >= -TOL && qd.s <= cd.L + TOL;
        });
        gasit = { i, e, fel: "T", j, cruce };
      });
      out.push(continua ? { i, e, fel: "drept" } : (gasit ?? { i, e, fel: "final" }));
    }
  });
  return out;
}

/** Perechile de drumuri PARALELE decalate: întoarcerea (se termină pe aceeași
 *  parte, lipite) și S-ul (unul se continuă în celălalt). */
export function perechile(axe: RoadAxis[]): Pereche[] {
  const cadre = axe.map(cadru);
  const out: Pereche[] = [];
  for (let i = 0; i < axe.length; i++) {
    for (let j = i + 1; j < axe.length; j++) {
      const a = axe[i], b = axe[j], c = cadre[i], cb = cadre[j];
      if (Math.abs(c.ux * cb.ux + c.uy * cb.uy) < 0.9) continue;
      const q1 = inCadru(a, c, [b.x1, b.y1]), q2 = inCadru(a, c, [b.x2, b.y2]);
      const t = (q1.t + q2.t) / 2;
      if (Math.abs(t) < 0.3 || Math.abs(t) > c.h + cb.h + 0.5) continue;    // pe aceeași axă, sau departe
      const s0 = Math.min(q1.s, q2.s), s1 = Math.max(q1.s, q2.s);
      const suprapunere = Math.min(c.L, s1) - Math.max(0, s0);
      if (suprapunere < 1.0) continue;
      const acelasiCapat = Math.abs(s1 - c.L) < 1.0 || Math.abs(s0) < 1.0;
      const seContinua = (s1 > c.L + 1.0 && s0 > 1.0) || (s0 < -1.0 && s1 < c.L - 1.0);
      if (seContinua && suprapunere <= 4 * RACORD) out.push({ i, j, fel: "S" });
      else if (acelasiCapat) out.push({ i, j, fel: "intoarcere" });
    }
  }
  return out;
}

const semnMuchie = (cb: Cadru, afara: Pt) =>
  (afara[0] * cb.nx + afara[1] * cb.ny) > 0 ? -1 : 1;

/** Arcul de la `pFrom` la `pTo` în jurul lui `c`, cu raza `r`, pe drumul scurt. */
function arc(pFrom: Pt, pTo: Pt, c: Pt, r: number): string {
  const cross = (pFrom[0] - c[0]) * (pTo[1] - c[1]) - (pFrom[1] - c[1]) * (pTo[0] - c[0]);
  return `A${f(r)} ${f(r)} 0 0 ${cross > 0 ? 1 : 0} ${P(pTo)}`;
}

const PROBE = 16;

/** Puncte pe același arc, ca să-l încercăm pe asfalt. */
function puncteArc(pFrom: Pt, pTo: Pt, c: Pt, r: number): Pt[] {
  const a0 = Math.atan2(pFrom[1] - c[1], pFrom[0] - c[0]);
  let da = Math.atan2(pTo[1] - c[1], pTo[0] - c[0]) - a0;
  while (da > Math.PI) da -= 2 * Math.PI;
  while (da < -Math.PI) da += 2 * Math.PI;
  return Array.from({ length: PROBE + 1 }, (_, k) =>
    [c[0] + r * Math.cos(a0 + (da * k) / PROBE), c[1] + r * Math.sin(a0 + (da * k) / PROBE)] as Pt);
}

function puncteBezier(p0: Pt, p1: Pt, p2: Pt, p3: Pt): Pt[] {
  return Array.from({ length: PROBE + 1 }, (_, k) => {
    const t = k / PROBE, u = 1 - t;
    const a = u * u * u, b = 3 * u * u * t, c = 3 * u * t * t, d = t * t * t;
    return [a * p0[0] + b * p1[0] + c * p2[0] + d * p3[0],
            a * p0[1] + b * p1[1] + c * p2[1] + d * p3[1]] as Pt;
  });
}

const puncteSegment = (p0: Pt, p1: Pt): Pt[] => puncteBezier(p0, p0, p1, p1);

/**
 * `corpuri[i]` e poligonul drumului cu axa `axe[i]` (lipsă = nu se știe), iar
 * `trasee` sunt aleile cu axa curbă sau frântă. Împreună cu `asfalt`
 * (drumurile, inclusiv piesele fără axă, și rampele) dau tăierea generică a
 * axelor: în corpul altor drumuri se taie numai corpurile DRUMURILOR
 * (`corpuri` + corpurile traseelor), nu și rampele — palierul rampei își are
 * axa lui, iar aleea care intră în gura ei continuă până la capăt (`drept`).
 */
export function carosabil(axe: RoadAxis[], rampe: number[][][] = [], lot: Pt[] = [],
                          { curburi = true, asfalt = [], corpuri = [], trasee = [], goluri = [] }:
                            { curburi?: boolean; asfalt?: number[][][];
                              corpuri?: (number[][] | undefined)[]; trasee?: Traseu[];
                              goluri?: number[][][] } = {}): Carosabil {
  const cadre = axe.map(cadru);
  const inele = asfalt.filter((r) => r.length >= 3);
  // corpurile drumurilor în care se taie capetele axelor (v. nota din antet);
  // în varianta fără curburi axa merge dinadins până la axa celuilalt drum
  // (Oana, 30.09.2026), deci acolo nu se taie
  const corpuriDrum = curburi
    ? [...corpuri, ...trasee.map((t) => t.corp)].filter((c): c is number[][] => !!c && c.length >= 3)
    : [];
  const capete = capetele(axe, rampe, lot, inele);
  const perechi = perechile(axe);
  const la = (i: number, e: 1 | 2) => capete.find((k) => k.i === i && k.e === e)!;
  // pe ORICE drum cu dublu sens, oricat de scurt: palierul rampei si racordul
  // «capat R» de 10 m ramaneau fara axa, iar linia se rupea (Oana, 29.09.2026)
  const cuAxa = (i: number) => axe[i].twoWay;
  // axa punctată a fiecărui drum: [d0, d1], tăiată la T, colț, întoarcere, S
  const d0 = axe.map(() => 0), d1 = axe.map((_, i) => cadre[i].L);
  const curbe: string[] = [];
  const liniiCurbe: Pt[][] = [];
  /** Curba intră doar dacă stă toată pe asfalt (fără asfalt dat, intră oricum). */
  const curba = (d: string, puncte: Pt[]) => {
    if (totPeAsfalt(puncte, inele)) { curbe.push(d); liniiCurbe.push(puncte); }
  };
  const pune = (i: number, s: number, dinspre: "start" | "capat") => {
    if (dinspre === "start") d0[i] = Math.max(d0[i], s); else d1[i] = Math.min(d1[i], s);
  };
  const colturi = new Set<string>();

  axe.forEach((a, i) => {
    const c = cadre[i];
    for (const e of [1, 2] as const) {
      const k = la(i, e);
      if (k.fel !== "colt" && k.fel !== "T") continue;
      const b = axe[k.j!], cb = cadre[k.j!];
      const dir = e === 2 ? 1 : -1;
      const afara: Pt = [dir * c.ux, dir * c.uy];
      const semn = semnMuchie(cb, afara);
      const sAprop = inCadru(a, c, punct(b, cb, 0, semn * cb.h)).s;
      const sDep = inCadru(a, c, punct(b, cb, 0, -semn * cb.h)).s;
      const capat = e === 2 ? "capat" : "start";
      const sInt = sAprop - dir * RACORD;
      if (!curburi) {
        // fără curburi: axa merge dreaptă până la axa celuilalt drum (sau prin
        // el, la cruce); niciun arc
        // (`pune` doar scurtează; aici axa se PRELUNGEȘTE peste capătul piesei)
        const sAxa = k.fel === "T" && k.cruce ? sDep : sAprop + dir * cb.h;
        if (e === 2) d1[i] = sAxa; else d0[i] = sAxa;
        continue;
      }
      if (k.fel === "T") {
        // la cruce axa se ÎNTINDE dreaptă prin celălalt drum; altfel se oprește
        // cu RACORD înainte, de unde pleacă arcele
        if (k.cruce) { if (e === 2) d1[i] = sDep; else d0[i] = sDep; }
        else pune(i, sInt, capat);
        if (cuAxa(i) && cuAxa(k.j!)) {
          // câte un arc de RACORD + w/2 pe fiecare parte, concentric cu racordarea
          for (const lat of [1, -1]) {
            const centru = punct(a, c, sInt, lat * (c.h + RACORD));
            const Rc = RACORD + c.h;
            const pa = punct(a, c, sInt, 0);
            const pb = punct(a, c, sInt + dir * Rc, lat * (c.h + RACORD));
            curba(`M${P(pa)} ${arc(pa, pb, centru, Rc)}`, puncteArc(pa, pb, centru, Rc));
          }
        }
        continue;
      }
      pune(i, sInt, capat);
      const cheie = `${Math.min(i, k.j!)}:${Math.max(i, k.j!)}`;
      if (colturi.has(cheie)) continue;
      colturi.add(cheie);
      if (!cuAxa(i) || !cuAxa(k.j!)) continue;
      // arcul: concentric cu racordarea concavă, de pe axa lui `a` pe axa lui `b`
      const latB = Math.sign(inCadru(a, c, punct(b, cb, cb.L / 2, 0)).t) || 1;
      const centru = punct(a, c, sInt, latB * (c.h + RACORD));
      const Rc = RACORD + c.h;
      const pa = punct(a, c, sInt, 0);
      const pb = punct(a, c, sInt + dir * Rc, latB * (c.h + RACORD));
      curba(`M${P(pa)} ${arc(pa, pb, centru, Rc)}`, puncteArc(pa, pb, centru, Rc));
    }
  });

  for (const { i, j, fel } of perechi) {
    const a = axe[i], b = axe[j], c = cadre[i], cb = cadre[j];
    const q1 = inCadru(a, c, [b.x1, b.y1]), q2 = inCadru(a, c, [b.x2, b.y2]);
    const t = (q1.t + q2.t) / 2;
    const s0 = Math.min(q1.s, q2.s), s1 = Math.max(q1.s, q2.s);
    // un punct de pe axa lui `b` dat prin `s` pe axa lui `a`, și `s`-ul lui în cadrul lui `b`
    const peB = (s: number): Pt => punct(a, c, s, t);
    const sB = (s: number) => inCadru(b, cb, peB(s)).s;
    // taie axa lui `b` la `s` (pe axa lui `a`), păstrând partea cu `s` mai mare
    // sau mai mic; în cadrul lui `b` asta e «start» sau «capăt» după sensul lui
    const acelasiSens = c.ux * cb.ux + c.uy * cb.uy > 0;
    const taieB = (s: number, pastreazaMare: boolean) =>
      pune(j, sB(s), pastreazaMare === acelasiSens ? "start" : "capat");
    if (fel === "intoarcere") {
      // semicerc între axe, la capătul comun, cu raza jumătate din distanța dintre ele
      const laCapat2 = Math.abs(s1 - c.L) < 1.0;
      const sE = laCapat2 ? Math.min(c.L, s1) : Math.max(0, s0);
      const r = Math.abs(t) / 2;
      const sC = laCapat2 ? sE - r : sE + r;
      if (!curburi) {
        // fără curburi: axele merg până la capăt și se leagă printr-un segment
        pune(i, sE, laCapat2 ? "capat" : "start");
        taieB(sE, !laCapat2);
        if (cuAxa(i) || cuAxa(j)) {
          const p0 = punct(a, c, sE, 0), p1 = peB(sE);
          curba(`M${P(p0)} L${P(p1)}`, puncteSegment(p0, p1));
        }
        continue;
      }
      pune(i, sC, laCapat2 ? "capat" : "start");
      taieB(sC, !laCapat2);
      if (cuAxa(i) || cuAxa(j)) {
        const pa = punct(a, c, sC, 0), pb = peB(sC), centru = punct(a, c, sC, t / 2);
        curba(`M${P(pa)} ${arc(pa, pb, centru, r)}`, puncteArc(pa, pb, centru, r));
      }
      continue;
    }
    // S: `a` se termină unde începe `b` (sau invers); curba merge de pe axa lui
    // `a` pe axa lui `b`, pe suprapunere plus S_INTINDERE la fiecare capăt
    const aSeTermina = s1 > c.L + 1.0;                 // b continuă dincolo de capătul lui a
    const sIn = aSeTermina ? s0 - S_INTINDERE : s1 + S_INTINDERE;   // pe a
    const sOut = aSeTermina ? c.L + S_INTINDERE : -S_INTINDERE;     // pe b
    pune(i, sIn, aSeTermina ? "capat" : "start");
    taieB(sOut, aSeTermina);
    if (cuAxa(i) || cuAxa(j)) {
      const mid = (sIn + sOut) / 2;
      const p0 = punct(a, c, sIn, 0), p1 = punct(a, c, mid, 0), p2 = peB(mid), p3 = peB(sOut);
      // fără curburi: segment oblic de pe o axă pe cealaltă, în loc de S
      if (curburi) curba(`M${P(p0)} C${P(p1)} ${P(p2)} ${P(p3)}`, puncteBezier(p0, p1, p2, p3));
      else curba(`M${P(p0)} L${P(p3)}`, puncteSegment(p0, p3));
    }
  }

  const drepte: string[] = [];
  const liniiDrepte: Pt[][] = [];
  const sageti: Sageata[] = [];
  const cale = (l: Pt[]) => `M${P(l[0])}` + l.slice(1).map((p) => ` L${P(p)}`).join("");
  axe.forEach((a, i) => {
    if (cuAxa(i)) {
      if (d1[i] - d0[i] <= 1) return;
      const p0 = punct(a, cadre[i], d0[i], 0), p1 = punct(a, cadre[i], d1[i], 0);
      for (const l of taieAxa([p0, p1], corpuriDrum, inele, corpuri[i], goluri)) {
        drepte.push(cale(l));
        liniiDrepte.push(l);
      }
      return;
    }
    // sens unic: săgeți pe axa ÎNTREAGĂ a piesei (nu pe cea scurtată la T și
    // colț, care e a liniuțelor), tăiată la corpul altor drumuri și la asfalt;
    // în sensul x1,y1 → x2,y2, adică al axei din document
    const intreaga: Pt[] = [[a.x1, a.y1], [a.x2, a.y2]];
    for (const l of taieAxa(intreaga, corpuriDrum, inele, corpuri[i], goluri)) sageti.push(...sagetile(l));
  });
  // traseele curbe: liniuțele pe polilinia lor, tăiate la fel; cu sens unic,
  // săgețile în loc de liniuțe
  for (const t of trasee) {
    for (const l of taieAxa(t.linie, corpuriDrum, inele, t.corp, goluri)) {
      if (t.twoWay === false) { sageti.push(...sagetile(l)); continue; }
      drepte.push(cale(l));
      liniiDrepte.push(l);
    }
  }
  return { axe: [...drepte, ...curbe], linii: [...liniiDrepte, ...liniiCurbe], sageti, capete, perechi };
}
